import {createMemoryHistory, createRouter, createWebHashHistory, Router, RouteRecordRaw,} from "vue-router";
import {crudPath} from "@/util/request";
import {app} from "@/models/App";
import Home from "@/views/home/Home.vue";
import {routes as otherRoutes} from "./routes";
import {mergeRoutes} from "@/util/router";
import develop from "@/router/develop";
import home from "@/router/home";
import {tokenManager} from "@/util/token";
import {toLogin} from "@/util/login";
import _ from "lodash";
import management from "@/router/management";
import CrudForm from "@/components/business/crud/CrudForm.vue";
import CrudTable from "@/components/business/crud/CrudTable.vue";
import { Locker } from '@/util/Locker';

interface PageResolved {
    [name: string]: {
        layout?: string;
        children: PageResolved;
    };
}

/**
 * view文件夹下的页面
 * 不会将文件夹名为component下的文件作为页面
 * 名为Layout.vue的页面将作为子路径的父路由
 */
export function getViewPage() {
    const context = require.context(
      "@/views",
      true,
      /(?<!component)\/\w+\.vue/,
      "sync"
    );
    const pages: PageResolved = {};
    context.keys().map((key) => {
        const path: string[] = [];
        let pageName: string | undefined;
        key
          .split("/")
          .slice(1)
          .map((item, index, arr) => {
              if (index === arr.length - 1) {
                  pageName = item;
              } else {
                  path.push("children", item);
              }
          });
        if (!pageName) {
            throw new Error("根据正则匹配，文件名不可能空，不会运行到此处。");
        }
        if (pageName === "Layout.vue") {
            _.set(pages, [...path, "children", "layout"], context(key).default);
        } else {
            _.set(pages, [...path, pageName.slice(0, -4)], context(key).default);
        }
    });
    return pages;
}

const homeRoute: RouteRecordRaw = {
    path: "/",
    name: "home",
    component: Home,
    children: mergeRoutes(home, develop, management),
};

const routes: RouteRecordRaw[] = [homeRoute, ...otherRoutes];

const router = createRouter({
    history: createWebHashHistory(process.env.BASE_URL),
    routes,
});

addGuard(router);

function addHomeRoute(route: RouteRecordRaw) {
    if (router.resolve(route.path).matched.length === 0) {
        router.addRoute("home", route);
        if (!homeRoute.children) {
            homeRoute.children = [];
        }
        homeRoute.children.push(route);
    }
}

function setCrudRoute(
  moduleName: string,
  controllerName: string,
  actionName: string,
  component: any,
  props: any
) {
    const routePath = crudPath(moduleName, controllerName, actionName);
    addHomeRoute({
        path: routePath,
        component,
        props,
    });
}

async function loadDynamicRoutes() {
    if (!await app.init()) {
        return false
    }
    for (const moduleName in app.data.widgetMap) {
        const module = app.data.widgetMap[moduleName];
        for (const controllerName in module) {
            const schema = module[controllerName];
            const props = {
                moduleName,
                controllerName,
            };
            if (schema.index) {
                setCrudRoute(moduleName, controllerName, "index", CrudTable, props);
            }
            if (schema.create) {
                setCrudRoute(moduleName, controllerName, "create", CrudForm, Object.assign({scene: "create"}, props));
            }
            if (schema.update) {
                setCrudRoute(
                  moduleName,
                  controllerName,
                  "update",
                  CrudForm,
                  Object.assign({scene: "update"}, props)
                );
            }
            if (schema.view) {
                setCrudRoute(
                  moduleName,
                  controllerName,
                  "view",
                  CrudForm,
                  Object.assign({scene: "view"}, props)
                );
            }
        }
    }
    return true
}

function addGuard(target: Router) {
    target.beforeEach(async (to) => {
        if (
          !tokenManager.get() &&
          !to.meta.noLogin
        ) {
            await toLogin();
        }
    });
}

let initRouter = false;
const locker = new Locker()

/**
 * 添加业务路由
 */
export async function addBizRouter() {
    await locker.lock(async () => {
        if (!initRouter && await loadDynamicRoutes()) {
            initRouter = true;
        }
    })
}

export async function getRouter() {
    return router;
}

export const createMemoryRouter = () => {
    const mRouter = createRouter({
        history: createMemoryHistory(),
        routes,
    });
    addGuard(mRouter);
    return mRouter;
};
